// Who the organization is invoiced as — the invoicing service's customer, read and written
// through. `tenancy.billing.manage` on both.
//
// The identity is not a column of this database (`billing-ledger.md` D15), so every field is
// `null` until the organization has a customer over there, which its first saved profile or its
// first top-up creates. A deployment naming no invoicing service answers `503` on both.

import { api } from '../client'
import type { components } from '../schema'

export type BillingProfileView = components['schemas']['BillingProfileView']
export type SetBillingProfileRequest = components['schemas']['SetBillingProfileRequest']
export type BillingPartyKind = components['schemas']['BillingPartyKind']
export type BillingAddress = components['schemas']['BillingAddress']

/**
 * The identity. **A `GET` creates nothing**: an organization that has never bought anything reads
 * a profile whose every field is `null`, rather than having a record made at a service we do not
 * own out of an identity nobody typed.
 */
export function readBillingProfile(signal?: AbortSignal): Promise<BillingProfileView> {
  return api.get<BillingProfileView>('/orgs/current/billing-profile', undefined, signal)
}

/**
 * Set it, whole. A `PUT` and not a `PATCH`: a first save is what creates the customer, so it has
 * to carry the identity entire, and an omitted field clears rather than preserving.
 *
 * `kind` is read only at the creation — whether the invoiced party is a person or a company
 * decides whose name a document is issued in, and the invoicing service does not let it change
 * afterwards. Changing `vat_number` consults VIES synchronously, so that save is the slow one and
 * may come back with no verdict (`vat_valid: null`) when a member state is unavailable.
 */
export function updateBillingProfile(body: SetBillingProfileRequest): Promise<BillingProfileView> {
  return api.put<BillingProfileView>('/orgs/current/billing-profile', body)
}
